import type {
  ThunkDispatch as ReduxThunkDispatch,
  UnknownAction,
} from '@reduxjs/toolkit';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  addRecentSearch,
  gisLayerFor,
  gisSublayerFor,
  type GISStateLayer,
  loadGISLayer,
  selectActiveLayers,
  selectGeocodeService,
  selectGISLayers,
  updateLayer,
} from '@rsos/reduxDataStore';
import { notifyDefault } from '@rsos/utils/toastUtils';
import useCenterPointCurrentView from '@rsos/utils/useCenterPointCurrentView';
import type { RootState } from '../../../../app';
import type { ThunkDispatch as GeocodeThunkDispatch } from './GlobalMapSearch.poi';
import type {
  AllTextMessages,
  MapCenter,
  SearchResultItem,
  TranscriptionsByCallId,
} from './GlobalMapSearch.types';
import { getSublayerKind } from './GlobalMapSearch.utils';
import CommandPalette from './command/CommandPalette';
import useCommandMode from './command/useCommandMode';
import { buildRecentEntryFromPrepared } from './recentSearchEntry';
import { runGeocodeSearch } from './search/geocode/geocodeSearch';
import { buildGisLayerSearchUnits } from './search/gis/gisLayerSearch';
import { runMessagesSearch } from './search/messages/messagesSearch';
import { runTranscriptsSearch } from './search/transcripts/transcriptsSearch';
import type { PreparedItem } from './useGroupedResults';
import useGroupedResults from './useGroupedResults';
import useResultActions from './useResultActions';

/**
 * Imperative surface the parent shell talks to. Keeps the slash
 * workflow's state private to this component while still letting the
 * shared `<Input>` route key events / clear actions through.
 */
export interface SlashExperienceHandle {
  /** Forwarded keystrokes — only consumed while the palette is shown. */
  onKeyDown: (e: React.KeyboardEvent) => void;
  /** Wipe slash-only state (called from the shell's clear button). */
  onClear: () => void;
  /** Re-open the palette on input focus (after a scoped-pick close). */
  onFocus: () => void;
}

interface SlashCommandExperienceProps {
  searchTerm: string;
  setSearchTerm: (next: string) => void;
  /**
   * True while the input does NOT start with `/`. The component still
   * mounts (so its hooks/state survive a round-trip through regular
   * mode) but skips the dropdown render.
   */
  isHidden: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  isFocusedRef: React.MutableRefObject<boolean>;
  /**
   * Called after a successful slash submit (action exec, scoped-result
   * click, arg pick). The shell uses this to close the regular dropdown
   * (which may have been left open from before slash mode), optionally
   * clear the input, and blur the search bar.
   *
   * Pass `preserveInput: true` for scoped-result selections so the
   * query the user typed stays in the bar after they pick a hit.
   */
  onComplete: (opts?: { preserveInput?: boolean }) => void;
  /**
   * Bubbles the inline-completion suffix that should render as ghost
   * text right after the typed input. Mirrors what `Tab` will fill,
   * which is always the *first* suggestion (catalog or arg). Empty
   * string when there's nothing to complete (no match / scoped-result
   * view / not in slash mode).
   */
  onGhostSuffixChange?: (next: string) => void;
}

const isParcelLayer = (layer: GISStateLayer): boolean => {
  if (layer.title?.toLowerCase().includes('parcel')) return true;
  return layer.sublayers.some(sl => getSublayerKind(sl.title) === 'parcel');
};

const setLayerVisibleWithValidSublayers = (layer: GISStateLayer): void => {
  const gisLayer = gisLayerFor(layer);
  if (!gisLayer) return;
  gisLayer.visible = true;
  const excludedIds = layer.filteredSublayerIds || [];
  for (const sublayer of layer.sublayers) {
    const gisSublayer = gisSublayerFor(sublayer);
    if (!gisSublayer) continue;
    gisSublayer.visible = !excludedIds.includes(sublayer.uniqueId);
  }
};

const PARCEL_LAYER_TURN_ON_TOAST_ID = 'globalmapsearch-parcel-layer-turn-on';
const SCOPED_COMMAND_MIN_QUERY_LENGTH = 3;
const LOCATION_SEARCH_DEBOUNCE_MS = 300;

/**
 * Owns the entire `/`-prefix workflow end-to-end: command parsing,
 * argument autocomplete, scoped-search execution (`/transcript`,
 * `/chat`, `/parcel`, `/location`), the palette UI, and all keyboard
 * handling for those
 * surfaces. Shares only the input element and focus refs with the
 * shell — every other piece of state and every effect is local.
 */
const SlashCommandExperience = forwardRef<
  SlashExperienceHandle,
  SlashCommandExperienceProps
>(function SlashCommandExperience(
  {
    searchTerm,
    setSearchTerm,
    isHidden,
    inputRef,
    isFocusedRef,
    onComplete,
    onGhostSuffixChange,
  },
  ref,
) {
  const store = useStore();
  const dispatch =
    useDispatch<ReduxThunkDispatch<RootState, undefined, UnknownAction>>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mapCenter: MapCenter = useCenterPointCurrentView();
  const geocodeService = useSelector(selectGeocodeService) as {
    gis_service_url?: string;
    token?: string | null;
  } | null;

  // Selectors needed by the `/parcel` GIS executor. Mirrored into refs so
  // the async search task always reads the current layer set / boundary even
  // if the user keeps typing while a request is in flight.
  const gisLayers = useSelector(selectGISLayers) as GISStateLayer[];
  const jurisdictionBoundary = useSelector(
    (state: RootState) =>
      (
        state as unknown as {
          geofences?: { currentGeofence?: { expanded_polygon?: unknown } };
        }
      ).geofences?.currentGeofence?.expanded_polygon ?? null,
  );
  const gisLayersRef = useRef(gisLayers);
  gisLayersRef.current = gisLayers;
  const mapCenterRef = useRef(mapCenter);
  mapCenterRef.current = mapCenter;
  const jurisdictionBoundaryRef = useRef<unknown>(jurisdictionBoundary);
  jurisdictionBoundaryRef.current = jurisdictionBoundary;

  // One AbortController per scoped async query (`/parcel`, `/location`) —
  // aborted on next keystroke so in-flight network calls stop trying to
  // write into stale state.
  const scopedSearchAbortRef = useRef<AbortController | null>(null);
  const locationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // --- Local state ---
  // Cursor is intentionally separate from the regular workflow's
  // cursor: each owns its own row index against its own list, so
  // arrow-keying inside the palette can't accidentally land on (or
  // be reset by) a row index that belongs to the recents/results
  // dropdown.
  const [cursor, setCursor] = useState(-1);

  // Lets the user dismiss the palette without clearing the input
  // (e.g. after picking a scoped result, where we keep the typed
  // query so they can pick another hit). Re-opens automatically on
  // any keystroke or refocus so the palette feels persistent.
  const [isPaletteOpen, setIsPaletteOpen] = useState(true);

  // --- Slash-command scoped-search state ---
  // `scopedResults` only ever holds rows produced by
  // `runScopedCommandSearch` and is rendered exclusively inside the
  // command palette; the regular `results` upstream is left untouched
  // while the user is typing a slash command.
  const [scopedResults, setScopedResults] = useState<SearchResultItem[]>([]);
  const [scopedIsSearching, setScopedIsSearching] = useState(false);
  const [scopedEmptyMessage, setScopedEmptyMessage] = useState<
    string | undefined
  >(undefined);
  const scopedGrouped = useGroupedResults(scopedResults, mapCenter, null);

  const cmd = useCommandMode(searchTerm);

  // Scoped rows are usually emitted in category order (`useGroupedResults`).
  // For `/location` specifically we surface pure geocode candidates and want
  // the palette weighted by proximity, so re-sort globally by distance.
  const scopedFlatItems = useMemo(() => {
    if (cmd.scopedFilter !== 'location') return scopedGrouped.visibleItems;
    return [...scopedGrouped.visibleItems].sort(
      (a, b) => a.distance - b.distance,
    );
  }, [cmd.scopedFilter, scopedGrouped.visibleItems]);

  // True when a scoped-search command (`/transcript`, `/chat`,
  // `/parcel`, `/location`) has a long-enough
  // query that scoped results are (or will be) populated. Drives the
  // palette into its scoped-results view and feeds the keyboard
  // navigation count.
  const isShowingScopedResults =
    !isHidden &&
    Boolean(cmd.scopedFilter) &&
    cmd.scopedQuery.length >= SCOPED_COMMAND_MIN_QUERY_LENGTH;

  // --- Click action for scoped-result rows ---
  // Same dispatch as the regular result click.
  const { handleResultClick: dispatchResultAction } = useResultActions({
    axonLayerData: undefined,
  });

  const handleScopedResultClick = useCallback(
    (prepared: PreparedItem) => {
      dispatchResultAction(prepared.item);
      // Mirror the regular result-click path and persist the
      // selection in the recent-searches surface so the user can
      // re-open this exact hit later. The query stored alongside
      // the entry is the scoped slash query (`cmd.scopedQuery`,
      // e.g. "main street") rather than the raw input ("/parcel
      // main street") — the recents UI matches against the same
      // shape the user re-types, and the entry's own icon / label /
      // sublabel come from `prepared` regardless.
      dispatch(
        addRecentSearch(
          buildRecentEntryFromPrepared(cmd.scopedQuery, prepared),
        ),
      );
      // Keep the user's typed query in the bar after they pick a
      // scoped result — mirrors the regular result-click path and
      // lets them pick another hit from the same search without
      // having to retype. Close the palette so the user can interact
      // with whatever they just selected; refocusing the bar (or
      // typing) will re-open it via the effects below.
      setIsPaletteOpen(false);
      setCursor(-1);
      onComplete({ preserveInput: true });
    },
    [dispatchResultAction, dispatch, cmd.scopedQuery, onComplete],
  );

  // Any keystroke that mutates the slash query should re-open the
  // palette (covers the case where the user picked a scoped result,
  // closed the palette, then started editing the preserved query
  // to refine the search).
  useEffect(() => {
    if (!isHidden) setIsPaletteOpen(true);
  }, [isHidden, searchTerm]);

  // --- Scoped slash-command search runner ---
  // Drives `/transcript`, `/chat`, `/parcel`, and `/location` on a private
  // pipeline that never touches the regular search state:
  //   - writes into `scopedResults` / `scopedIsSearching` so the
  //     regular dropdown stays whatever the user last left it as.
  //   - runs only the executor that owns the scoped category (no
  //     classifier and no cross-source orchestrator fan-out).
  //   - does not call `clearHighlightedFeature` (no map features to
  //     reset). The scoped-result click path *does* persist the
  //     selection into the recent-searches surface, same as the
  //     regular result click — see `handleScopedResultClick`.
  // Transcript / chat are synchronous (in-memory scans). Parcel hits
  // live ArcGIS feature services via `buildGisLayerSearchUnits` with
  // `primary='parcel'`, which both restricts to parcel sublayers and
  // uses the parcel-specific field patterns. Location runs geocode only.
  const ensureParcelLayersActive = useCallback(
    async (signal: AbortSignal): Promise<GISStateLayer[]> => {
      const parcelLayers = gisLayersRef.current.filter(isParcelLayer);
      if (parcelLayers.length === 0) {
        return selectActiveLayers(
          store.getState() as Parameters<typeof selectActiveLayers>[0],
        ) as GISStateLayer[];
      }

      let didActivateAnyParcelLayer = false;
      for (const layer of parcelLayers) {
        if (signal.aborted) break;
        let resolvedLayer = layer;
        if (!resolvedLayer.loaded && !resolvedLayer.loading) {
          try {
            const { loadedLayer } = await dispatch(
              loadGISLayer({ service: resolvedLayer.service }),
            ).unwrap();
            resolvedLayer = loadedLayer;
          } catch {
            continue;
          }
        }

        if (signal.aborted) break;
        const latestLayers = selectGISLayers(
          store.getState() as Parameters<typeof selectGISLayers>[0],
        ) as GISStateLayer[];
        const latestLayer =
          latestLayers.find(current => current.id === resolvedLayer.id) ??
          resolvedLayer;
        if (latestLayer.active) continue;
        setLayerVisibleWithValidSublayers(latestLayer);
        dispatch(updateLayer({ layerID: latestLayer.id, active: true }));
        didActivateAnyParcelLayer = true;
      }

      if (didActivateAnyParcelLayer) {
        notifyDefault({
          message: 'Turning on parcel layer',
          noIcon: true,
          options: {
            toastId: PARCEL_LAYER_TURN_ON_TOAST_ID,
            autoClose: 1500,
            closeButton: false,
            style: { opacity: 0.65 },
          },
        });
      }

      return selectActiveLayers(
        store.getState() as Parameters<typeof selectActiveLayers>[0],
      ) as GISStateLayer[];
    },
    [dispatch, store],
  );

  const runScopedCommandSearch = useCallback(
    (scopedFilter: string, query: string) => {
      // Always abort any in-flight scoped async run before starting a new
      // search (or short-circuiting on a too-short query) so its
      // late-arriving response can't overwrite cleared state.
      scopedSearchAbortRef.current?.abort();
      scopedSearchAbortRef.current = null;
      if (locationDebounceRef.current) {
        clearTimeout(locationDebounceRef.current);
        locationDebounceRef.current = null;
      }

      // Always clear visible rows immediately when the query changes so stale
      // hits from the previous input can't be mistaken for current results.
      setScopedResults([]);
      setScopedEmptyMessage(undefined);

      if (query.length < SCOPED_COMMAND_MIN_QUERY_LENGTH) {
        setScopedResults([]);
        setScopedIsSearching(false);
        return;
      }

      const collected: SearchResultItem[] = [];
      const flush = () => {
        setScopedResults([...collected]);
      };

      setScopedIsSearching(true);

      const snapshot = store.getState() as unknown as {
        transcriptions?: { calls?: TranscriptionsByCallId };
        talkjsMessages?: { allTextMessages?: AllTextMessages };
      };

      try {
        switch (scopedFilter) {
          case 'transcript': {
            const transcriptions = snapshot.transcriptions?.calls;
            const hasTranscriptData = Boolean(
              transcriptions && Object.keys(transcriptions).length > 0,
            );
            runTranscriptsSearch({
              text: query,
              transcriptions,
              collected,
              flush,
            });
            setScopedResults([...collected]);
            setScopedIsSearching(false);
            setScopedEmptyMessage(
              !hasTranscriptData && collected.length === 0
                ? 'Unable to find data.'
                : undefined,
            );
            return;
          }
          case 'message': {
            const messages = snapshot.talkjsMessages?.allTextMessages;
            const hasMessageData = Boolean(
              messages && Object.keys(messages).length > 0,
            );
            runMessagesSearch({
              text: query,
              messages,
              collected,
              flush,
            });
            setScopedResults([...collected]);
            setScopedIsSearching(false);
            setScopedEmptyMessage(
              !hasMessageData && collected.length === 0
                ? 'Unable to find data.'
                : undefined,
            );
            return;
          }
          case 'parcel': {
            const controller = new AbortController();
            scopedSearchAbortRef.current = controller;
            void (async () => {
              try {
                const activeLayers = await ensureParcelLayersActive(
                  controller.signal,
                );
                if (controller.signal.aborted) return;
                const units = buildGisLayerSearchUnits({
                  text: query,
                  // Force the parcel-only field patterns + skip every
                  // non-parcel sublayer regardless of what the AI would
                  // have classified the raw text as.
                  primary: 'parcel',
                  normalizedPhone: null,
                  layers: activeLayers,
                  signal: controller.signal,
                  jurisdictionBoundaryRef,
                  mapCenterRef,
                  collected,
                  flush,
                });
                if (units.length === 0) {
                  // No parcel-capable layers loaded (or accessible) —
                  // nothing to await, surface a generic fallback.
                  if (scopedSearchAbortRef.current !== controller) return;
                  setScopedResults([]);
                  setScopedIsSearching(false);
                  setScopedEmptyMessage('Unable to find data.');
                  scopedSearchAbortRef.current = null;
                  return;
                }
                await Promise.all(units.map(u => u.run()));
                // A newer keystroke already replaced this controller, so
                // its results are stale — drop them on the floor.
                if (controller.signal.aborted) return;
                if (scopedSearchAbortRef.current !== controller) return;
                setScopedResults([...collected]);
                setScopedIsSearching(false);
                setScopedEmptyMessage(undefined);
                scopedSearchAbortRef.current = null;
              } catch {
                if (controller.signal.aborted) return;
                if (scopedSearchAbortRef.current !== controller) return;
                setScopedResults([]);
                setScopedIsSearching(false);
                setScopedEmptyMessage('Unable to find data.');
                scopedSearchAbortRef.current = null;
              }
            })();
            return;
          }
          case 'location': {
            // Geocode is network-heavy; debounce `/location` queries so
            // we don't issue a request on every keystroke.
            locationDebounceRef.current = setTimeout(() => {
              locationDebounceRef.current = null;
              const controller = new AbortController();
              scopedSearchAbortRef.current = controller;
              void (async () => {
                try {
                  const mapCenter = mapCenterRef.current;
                  await runGeocodeSearch({
                    text: query,
                    lat: mapCenter?.latitude,
                    lng: mapCenter?.longitude,
                    serviceUrl: geocodeService?.gis_service_url,
                    token: geocodeService?.token,
                    signal: controller.signal,
                    collected,
                    flush,
                    thunkDispatch: dispatch as unknown as GeocodeThunkDispatch,
                  });
                  if (controller.signal.aborted) return;
                  if (scopedSearchAbortRef.current !== controller) return;
                  setScopedResults([...collected]);
                  setScopedIsSearching(false);
                  setScopedEmptyMessage(undefined);
                  scopedSearchAbortRef.current = null;
                } catch {
                  if (controller.signal.aborted) return;
                  if (scopedSearchAbortRef.current !== controller) return;
                  setScopedResults([]);
                  setScopedIsSearching(false);
                  setScopedEmptyMessage('Unable to find data.');
                  scopedSearchAbortRef.current = null;
                }
              })();
            }, LOCATION_SEARCH_DEBOUNCE_MS);
            return;
          }
          default:
            // Defensive: an unrecognized scopedFilter would otherwise
            // leave the palette stuck in the searching state forever.
            setScopedResults([]);
            setScopedIsSearching(false);
            setScopedEmptyMessage('Unable to find data.');
            return;
        }
      } catch {
        // Any unexpected runtime issue in scoped mode should degrade to
        // an empty state rather than crashing the map search shell.
        setScopedResults([]);
        setScopedIsSearching(false);
        setScopedEmptyMessage('Unable to find data.');
      }
    },
    [dispatch, ensureParcelLayersActive, geocodeService, mapCenterRef, store],
  );

  // --- Scoped-search command bridge ---
  // Runs the scoped executor whenever a committed scoped command's
  // query changes. When the user leaves slash mode (cleared
  // the slash or switched to a non-scoped command) we wipe the scoped
  // state so a stale result set can't re-appear next time the palette
  // opens.
  useEffect(() => {
    if (cmd.scopedFilter) {
      runScopedCommandSearch(cmd.scopedFilter, cmd.scopedQuery);
    } else {
      if (locationDebounceRef.current) {
        clearTimeout(locationDebounceRef.current);
        locationDebounceRef.current = null;
      }
      scopedSearchAbortRef.current?.abort();
      scopedSearchAbortRef.current = null;
      setScopedResults([]);
      setScopedIsSearching(false);
      setScopedEmptyMessage(undefined);
    }
  }, [cmd.scopedFilter, cmd.scopedQuery, runScopedCommandSearch]);

  // Reset the keyboard cursor whenever the palette swaps between its
  // three views (catalog ↔ arg suggestions ↔ scoped results). Without
  // this the highlight would carry over to a different list and point
  // at the wrong row.
  useEffect(() => {
    setCursor(-1);
  }, [cmd.isArgMode, isShowingScopedResults]);

  // Keep the keyboard-selected row visible inside the palette.
  useEffect(() => {
    if (cursor < 0 || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelector(
      `[data-result-index="${cursor}"]`,
    ) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [cursor]);

  // --- Slash-command execute glue ---
  // Bridges the palette's `onExecute(cmdId)` to the underlying
  // `useCommandMode.execute`. Two flavors of post-execute UX:
  //   - scopedSearch: don't run anything yet; just auto-fill the
  //     input to `/<id> ` so the user can keep typing args, and the
  //     scoped-search bridge effect picks up the suffix.
  //   - action with arg suggestions: same autofill — picking the
  //     command in catalog mode without args isn't an execute, it's
  //     a "move into arg mode" gesture.
  //   - action without arg suggestions: dispatch via the registry,
  //     then close the bar (mirrors the post-result-click flow).
  const handleExecuteCommand = useCallback(
    async (cmdId: string) => {
      const def = cmd.suggestions.find(d => d.id === cmdId);
      if (!def) return;

      const wantsAutofill =
        def.kind === 'scopedSearch' ||
        (def.kind === 'action' && Boolean(def.getArgSuggestions));
      if (wantsAutofill) {
        const next = `/${def.id} `;
        setSearchTerm(next);
        setCursor(-1);
        // Keep focus so the user can immediately type the query.
        inputRef.current?.focus();
        return;
      }

      const result = await cmd.execute(cmdId);
      if (result.ok) onComplete();
      // Failure path is intentionally minimal in v1: leave the input
      // alone so the user can fix the arg. Future tiers can surface
      // `result.message` inline in the palette.
    },
    [cmd, setSearchTerm, inputRef, onComplete],
  );

  // Click / Enter on an arg-suggestion row. Fills the input with the
  // canonical value and runs the command in one shot — that's the
  // "select and execute" affordance. Tab uses `handleArgTabComplete`
  // below to fill without executing.
  const handlePickArg = useCallback(
    async (suggestion: { value: string }) => {
      const cmdDef = cmd.committedCommand;
      if (!cmdDef || cmdDef.kind !== 'action') return;
      // Reflect the picked value in the input so the user has an
      // accurate record of what just ran (and so the next keystroke
      // edits from the resolved value, not their partial).
      setSearchTerm(`/${cmdDef.id} ${suggestion.value}`);
      const result = await cmd.executeWithArg(cmdDef.id, suggestion.value);
      if (result.ok) onComplete();
    },
    [cmd, setSearchTerm, onComplete],
  );

  // Tab autofills the currently highlighted suggestion (or the first
  // row if the user hasn't arrowed onto anything yet). The cursor
  // and the inline ghost-text are kept in lock-step — whatever the
  // ghost previews is exactly what Tab will commit — so the user
  // can arrow up/down to "preview" different completions inline
  // without ever having to look at the dropdown.
  const handleArgTabComplete = useCallback(() => {
    if (cmd.isArgMode) {
      const cmdDef = cmd.committedCommand;
      const target =
        cmd.argSuggestions[cursor >= 0 ? cursor : 0] ?? cmd.argSuggestions[0];
      if (!cmdDef || !target) return;
      setSearchTerm(`/${cmdDef.id} ${target.value}`);
      setCursor(-1);
      inputRef.current?.focus();
      return;
    }
    const target =
      cmd.suggestions[cursor >= 0 ? cursor : 0] ?? cmd.suggestions[0];
    if (!target) return;
    setSearchTerm(`/${target.id} `);
    setCursor(-1);
    inputRef.current?.focus();
  }, [cmd, cursor, setSearchTerm, inputRef]);

  // --- Inline ghost-text completion ---
  // Computes the suffix that, when concatenated to the typed input,
  // produces what Tab would fill. Visually rendered by the shell as
  // muted text right after the cursor — gives the user a preview of
  // the completion without requiring a glance at the dropdown.
  //
  // Tracks the keyboard cursor so arrow-key movement live-updates
  // the inline preview to whatever row is highlighted (or the first
  // row when nothing is highlighted yet).
  //
  // Empty in cases where there's no meaningful completion to show:
  //   - the slash experience isn't visible
  //   - we're in scoped-results view (rows are search hits, not
  //     prefixed strings — no completion to ghost)
  //   - the highlighted suggestion's id/value isn't a prefix-extension
  //     of what the user has typed (e.g. typed `/xyz` → no command
  //     matches, no completion possible)
  const ghostSuffix = useMemo(() => {
    if (isHidden) return '';
    if (isShowingScopedResults) return '';
    const idx = cursor >= 0 ? cursor : 0;
    if (cmd.isArgMode) {
      const target = cmd.argSuggestions[idx] ?? cmd.argSuggestions[0];
      if (!target) return '';
      const typed = cmd.parsed.rest;
      // Only extend when the typed arg is a strict prefix of the
      // highlighted suggestion's canonical value — otherwise the
      // ghost would visually contradict what the user has typed.
      if (!target.value.toLowerCase().startsWith(typed.toLowerCase())) {
        return '';
      }
      return target.value.slice(typed.length);
    }
    // Catalog mode (typing the command name).
    const target = cmd.suggestions[idx] ?? cmd.suggestions[0];
    if (!target) return '';
    const typed = cmd.parsed.name;
    if (!target.id.toLowerCase().startsWith(typed.toLowerCase())) return '';
    // Trailing space mirrors what Tab fills (`/<id> `) so the user
    // can immediately type the arg without an extra keystroke.
    return `${target.id.slice(typed.length)} `;
  }, [
    isHidden,
    isShowingScopedResults,
    cursor,
    cmd.isArgMode,
    cmd.argSuggestions,
    cmd.suggestions,
    cmd.parsed.rest,
    cmd.parsed.name,
  ]);

  // useLayoutEffect (NOT useEffect): the bubbled suffix needs to land
  // in the shell's state *before* the browser paints, otherwise every
  // keystroke renders one frame with the new typed text against the
  // previous suffix — visible to the user as a flicker. Layout effects
  // fire after the DOM mutation that committed the new searchTerm but
  // before paint, so the shell's setState here triggers a second
  // synchronous render that lands in the same paint.
  useLayoutEffect(() => {
    onGhostSuffixChange?.(ghostSuffix);
  }, [ghostSuffix, onGhostSuffixChange]);

  // Wipe the bubbled suffix on unmount / when slash mode hides so a
  // stale ghost doesn't linger in the regular workflow.
  useEffect(() => {
    if (isHidden) onGhostSuffixChange?.('');
  }, [isHidden, onGhostSuffixChange]);

  // --- Imperative surface for the shell ---
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isHidden) return;

      // Tab (without modifiers) is the dedicated autocomplete-without-
      // execute affordance for the slash palette. Captured early so
      // browsers don't move focus off the input.
      if (
        e.key === 'Tab' &&
        !e.shiftKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        e.preventDefault();
        handleArgTabComplete();
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        // Slash-command mode owns Enter end-to-end. Order of priority
        // mirrors the visible palette body:
        //   - scoped-results view → pick the highlighted (or first)
        //     row and dispatch the same action a click would.
        //   - arg-suggestions view → fill input + execute the action.
        //   - catalog view → execute the highlighted command (which
        //     for action commands with arg suggestions or for scoped-
        //     search commands becomes "autofill the prefix").
        if (isShowingScopedResults) {
          if (scopedFlatItems.length > 0) {
            const idx = cursor >= 0 ? cursor : 0;
            const target = scopedFlatItems[idx] ?? scopedFlatItems[0];
            if (target) handleScopedResultClick(target);
          }
          return;
        }
        if (cmd.isArgMode) {
          if (cmd.argSuggestions.length > 0) {
            const target =
              cmd.argSuggestions[cursor >= 0 ? cursor : 0] ??
              cmd.argSuggestions[0];
            if (target) void handlePickArg(target);
          }
          return;
        }
        if (cmd.suggestions.length > 0) {
          const target = cmd.suggestions[cursor >= 0 ? cursor : 0];
          void handleExecuteCommand(target.id);
        }
        return;
      }

      if (e.key === 'Escape') {
        setCursor(-1);
        // Drop focus too so the recents surface doesn't immediately
        // re-open; users tapping Escape are signalling "done with the
        // bar".
        isFocusedRef.current = false;
        inputRef.current?.blur();
        return;
      }

      // Arrow nav row count follows the active palette view.
      let navCount = 0;
      if (isShowingScopedResults) navCount = scopedFlatItems.length;
      else if (cmd.isArgMode) navCount = cmd.argSuggestions.length;
      else navCount = cmd.suggestions.length;
      if (navCount === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor(prev => Math.min(prev + 1, navCount - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor(prev => {
          const next = prev - 1;
          if (next < 0) {
            inputRef.current?.focus();
            return -1;
          }
          return next;
        });
      }
    },
    [
      isHidden,
      isShowingScopedResults,
      scopedFlatItems,
      cmd.isArgMode,
      cmd.argSuggestions,
      cmd.suggestions,
      cursor,
      handleArgTabComplete,
      handleExecuteCommand,
      handlePickArg,
      handleScopedResultClick,
      inputRef,
      isFocusedRef,
    ],
  );

  const handleClear = useCallback(() => {
    if (locationDebounceRef.current) {
      clearTimeout(locationDebounceRef.current);
      locationDebounceRef.current = null;
    }
    scopedSearchAbortRef.current?.abort();
    scopedSearchAbortRef.current = null;
    setScopedResults([]);
    setScopedIsSearching(false);
    setScopedEmptyMessage(undefined);
    setCursor(-1);
    setIsPaletteOpen(true);
  }, []);

  const handleFocus = useCallback(() => {
    setIsPaletteOpen(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: handleKeyDown,
      onClear: handleClear,
      onFocus: handleFocus,
    }),
    [handleKeyDown, handleClear, handleFocus],
  );

  if (isHidden) return null;
  if (!isPaletteOpen) return null;

  return (
    <CommandPalette
      dropdownRef={dropdownRef}
      suggestions={cmd.suggestions}
      isCatalog={cmd.parsed.name === ''}
      isArgMode={cmd.isArgMode}
      argSuggestions={cmd.argSuggestions}
      argCommandId={cmd.committedCommand?.id}
      isScopedResultsMode={isShowingScopedResults}
      scopedKind={cmd.scopedFilter}
      scopedQuery={cmd.scopedQuery}
      scopedResults={scopedFlatItems}
      scopedIsSearching={scopedIsSearching}
      scopedEmptyMessage={scopedEmptyMessage}
      cursor={cursor}
      onCursorChange={setCursor}
      onExecute={handleExecuteCommand}
      onPickArg={handlePickArg}
      onPickScopedResult={handleScopedResultClick}
    />
  );
});

export default SlashCommandExperience;
